import { memo, useCallback, useEffect, useRef, useState } from "react";
import CardView, { CardViewProps } from "./NewsCardView";
import { ChevronRight, ChevronLeft } from "lucide-react";

export interface NewsCardLists {
  items: CardViewProps[];
  onLoadMore: () => void;
  hasMore: boolean;
  loading: boolean;
}

const NewsCardListView = memo(function NewsCardListView({
  items,
  onLoadMore,
  hasMore,
  loading,
}: NewsCardLists) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver>(null);
  const [isAtStart, setIsAtStart] = useState(true);

  const lastItemRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            onLoadMore();
          }
        },
        {
          root: scrollRef.current,
          rootMargin: "0px",
          threshold: 1.0,
        }
      );
      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, onLoadMore]
  );

  const scrollLeft = () => {
    const container = scrollRef.current;
    const cardViewContainer = document.querySelector('.card-view-container') as HTMLElement | null;
    const offsetWidth = cardViewContainer ? cardViewContainer.offsetWidth : 300;
    if (!container) return;
    container.scrollBy({ left: -(offsetWidth + 28), behavior: "smooth" });
  };


  
  const scrollRight = () => {
    const container = scrollRef.current;
    const cardViewContainer = document.querySelector('.card-view-container') as HTMLElement | null;
    const offsetWidth = cardViewContainer ? cardViewContainer.offsetWidth : 300;
    if (!container) return;
    container.scrollBy({ left: offsetWidth + 28, behavior: "smooth" });
  };

  const checkScrollPosition = useCallback(() => {
    if (scrollRef.current) {
      setIsAtStart(scrollRef.current.scrollLeft <= 0);
    }
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition();
      
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
      };
    }
  }, [checkScrollPosition]);

  return (
    <div className="new-card-list-container items-center px-0 py-8 w-full tablet:w-full tablet:mx-auto desktop:w-screen desktop:max-w-screen-desktop font-sans">
      <div
        ref={scrollRef}
        className="flex space-x-7 overflow-x-auto scrollbar-hide desktop:space-x-7"
      >
        {items.map((item, index) =>
          index === items.length - 1 ? (
            <div key={item.idx} ref={lastItemRef} className="flex-shrink-0 w-[80vw] max-w-[280px] h-[350px] tablet:w-[45vw] tablet:max-w-[300px] laptop:w-[300px]">
              <CardView response={item} />
            </div>
          ) : (
            <CardView key={item.idx} response={item}/>
          )
        )}
      </div>
      <div className="left-2 flex items-center justify-center pl-4 pt-4 space-x-20 laptop:space-x-40">

        <button
          onClick={scrollLeft}
          disabled={isAtStart}
          className={`new-card-list-left-scroll flex items-center justify-center w-12 h-12 rounded-3xl transition-colors ${
            isAtStart ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 cursor-pointer'
          }`}
        >
          <ChevronLeft size={24} className={isAtStart ? 'text-gray-500' : 'text-gray-600'} />
        </button>


        <button
          onClick={scrollRight}
          disabled={!hasMore}
          className={`new-card-list-right-scroll flex items-center justify-center w-12 h-12 rounded-3xl transition-colors ${
            !hasMore ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 cursor-pointer'
          }`}
        >
          <ChevronRight size={24} className={!hasMore ? 'text-gray-500' : 'text-gray-600'} />
        </button>
      </div>
    </div>
  );
});

export default NewsCardListView;
